import { openDB, type IDBPDatabase } from 'idb';
import { getCore } from '../data/content';
import {
  DEFAULT_COSMETICS,
  isCoreSkinId,
  isSkillSkinId,
  type CosmeticLoadout,
} from '../data/cosmetics';
import type { CoreId, MetaModifiers } from '../game/simulation/types';

export const SAVE_VERSION = 3;

export interface PlayerSettings {
  graphicsQuality: 'low' | 'medium' | 'high' | 'ultra';
  damageNumbers: boolean;
  reducedShake: boolean;
  reducedFlashes: boolean;
  haptics: boolean;
  textScale: 1 | 1.1 | 1.2;
  muted: boolean;
}

export interface MetaSaveV3 {
  schemaVersion: 3;
  coreCoins: number;
  profileLevel: number;
  profileXp: number;
  selectedCoreId: CoreId;
  coreMasteryXp: Record<CoreId, number>;
  cosmetics: CosmeticLoadout;
  unlockedBlueprints: string[];
  workshop: {
    power: number;
    integrity: number;
    insight: number;
  };
  settings: PlayerSettings;
  lifetime: {
    runs: number;
    victories: number;
    kills: number;
    bestWave: number;
    bestDamage: number;
    bestCombo: number;
  };
}

export interface RunResultInput {
  reward: number;
  profileXp: number;
  coreXp: number;
  coreId: CoreId;
  victory: boolean;
  wave: number;
  kills: number;
  damage: number;
  maxCombo: number;
  blueprintId: string | null;
}

export interface SaveRepository {
  load(): Promise<MetaSaveV3>;
  save(value: MetaSaveV3): Promise<void>;
  applyRunResult(result: RunResultInput): Promise<MetaSaveV3>;
  purchaseWorkshop(branch: keyof MetaSaveV3['workshop']): Promise<MetaSaveV3>;
  selectCore(coreId: CoreId): Promise<MetaSaveV3>;
  updateCosmetics(cosmetics: Partial<CosmeticLoadout>): Promise<MetaSaveV3>;
  updateSettings(settings: Partial<PlayerSettings>): Promise<MetaSaveV3>;
  reset(): Promise<MetaSaveV3>;
}

export const WORKSHOP_COSTS = [50, 75, 110, 160, 230] as const;
export const CORE_MASTERY_THRESHOLDS = [0, 90, 240, 450, 720, 1050] as const;

export function profileXpRequired(level: number): number {
  return 100 + Math.max(1, level) * 70;
}

export function coreMasteryLevel(xp: number): number {
  let level = 0;
  for (let index = 1; index < CORE_MASTERY_THRESHOLDS.length; index += 1) {
    if (xp >= CORE_MASTERY_THRESHOLDS[index]!) level = index;
  }
  return level;
}

export function createDefaultSave(): MetaSaveV3 {
  return {
    schemaVersion: 3,
    coreCoins: 0,
    profileLevel: 1,
    profileXp: 0,
    selectedCoreId: 'aegis',
    coreMasteryXp: { aegis: 0, tempest: 0, cryon: 0, singularity: 0 },
    cosmetics: { ...DEFAULT_COSMETICS },
    unlockedBlueprints: [],
    workshop: { power: 0, integrity: 0, insight: 0 },
    settings: {
      graphicsQuality: 'high',
      damageNumbers: true,
      reducedShake: false,
      reducedFlashes: false,
      haptics: true,
      textScale: 1,
      muted: false,
    },
    lifetime: {
      runs: 0,
      victories: 0,
      kills: 0,
      bestWave: 0,
      bestDamage: 0,
      bestCombo: 0,
    },
  };
}

function isCoreId(value: unknown): value is CoreId {
  return value === 'aegis' || value === 'tempest' || value === 'cryon' || value === 'singularity';
}

export function migrateSave(value: unknown): MetaSaveV3 {
  if (!value || typeof value !== 'object') return createDefaultSave();
  const candidate = value as Partial<Omit<MetaSaveV3, 'schemaVersion'>> & {
    schemaVersion?: number;
  };
  if (
    candidate.schemaVersion !== 1 &&
    candidate.schemaVersion !== 2 &&
    candidate.schemaVersion !== 3
  )
    return createDefaultSave();
  const base = createDefaultSave();
  const selectedCoreId = isCoreId(candidate.selectedCoreId) ? candidate.selectedCoreId : 'aegis';
  return {
    ...base,
    ...candidate,
    schemaVersion: 3,
    profileLevel: Math.max(1, Number(candidate.profileLevel) || 1),
    profileXp: Math.max(0, Number(candidate.profileXp) || 0),
    selectedCoreId,
    coreMasteryXp: {
      ...base.coreMasteryXp,
      ...(candidate.coreMasteryXp ?? {}),
    },
    cosmetics: {
      coreSkinId: isCoreSkinId(candidate.cosmetics?.coreSkinId)
        ? candidate.cosmetics.coreSkinId
        : DEFAULT_COSMETICS.coreSkinId,
      skillSkinId: isSkillSkinId(candidate.cosmetics?.skillSkinId)
        ? candidate.cosmetics.skillSkinId
        : DEFAULT_COSMETICS.skillSkinId,
    },
    unlockedBlueprints: Array.isArray(candidate.unlockedBlueprints)
      ? candidate.unlockedBlueprints.filter((item): item is string => typeof item === 'string')
      : [],
    workshop: { ...base.workshop, ...candidate.workshop },
    settings: { ...base.settings, ...candidate.settings },
    lifetime: { ...base.lifetime, ...candidate.lifetime },
  };
}

export function isCoreUnlocked(coreId: CoreId, save: MetaSaveV3): boolean {
  const definition = getCore(coreId);
  return (
    save.profileLevel >= definition.unlockLevel &&
    (definition.unlockBlueprintId === null ||
      save.unlockedBlueprints.includes(definition.unlockBlueprintId))
  );
}

export function toMetaModifiers(save: MetaSaveV3): MetaModifiers {
  const selectedCoreId = isCoreUnlocked(save.selectedCoreId, save) ? save.selectedCoreId : 'aegis';
  return {
    powerLevel: save.workshop.power,
    integrityLevel: save.workshop.integrity,
    insightLevel: save.workshop.insight,
    unlockedBlueprints: [...save.unlockedBlueprints],
    selectedCoreId,
    coreMasteryLevel: coreMasteryLevel(save.coreMasteryXp[selectedCoreId]),
  };
}

export class IndexedDbSaveRepository implements SaveRepository {
  private databasePromise: Promise<IDBPDatabase> | null = null;
  private memory = createDefaultSave();

  async load(): Promise<MetaSaveV3> {
    if (typeof indexedDB === 'undefined') return structuredClone(this.memory);
    try {
      const database = await this.database();
      const stored = await database.get('player', 'meta');
      const migrated = migrateSave(stored);
      if (!stored || (stored as { schemaVersion?: number }).schemaVersion !== SAVE_VERSION)
        await database.put('player', migrated, 'meta');
      return migrated;
    } catch {
      return structuredClone(this.memory);
    }
  }

  async save(value: MetaSaveV3): Promise<void> {
    this.memory = structuredClone(value);
    if (typeof indexedDB === 'undefined') return;
    const database = await this.database();
    await database.put('player', value, 'meta');
  }

  async applyRunResult(result: RunResultInput): Promise<MetaSaveV3> {
    const save = await this.load();
    save.coreCoins += Math.max(0, Math.round(result.reward));
    save.profileXp += Math.max(0, Math.round(result.profileXp));
    while (save.profileXp >= profileXpRequired(save.profileLevel)) {
      save.profileXp -= profileXpRequired(save.profileLevel);
      save.profileLevel += 1;
    }
    save.coreMasteryXp[result.coreId] += Math.max(0, Math.round(result.coreXp));
    save.lifetime.runs += 1;
    save.lifetime.victories += result.victory ? 1 : 0;
    save.lifetime.kills += Math.max(0, Math.round(result.kills));
    save.lifetime.bestWave = Math.max(save.lifetime.bestWave, result.wave);
    save.lifetime.bestDamage = Math.max(save.lifetime.bestDamage, Math.round(result.damage));
    save.lifetime.bestCombo = Math.max(save.lifetime.bestCombo, Math.round(result.maxCombo));
    if (result.blueprintId && !save.unlockedBlueprints.includes(result.blueprintId)) {
      save.unlockedBlueprints.push(result.blueprintId);
    }
    await this.save(save);
    return save;
  }

  async purchaseWorkshop(branch: keyof MetaSaveV3['workshop']): Promise<MetaSaveV3> {
    const save = await this.load();
    const currentLevel = save.workshop[branch];
    if (currentLevel >= WORKSHOP_COSTS.length) return save;
    const cost = WORKSHOP_COSTS[currentLevel]!;
    if (save.coreCoins < cost) return save;
    save.coreCoins -= cost;
    save.workshop[branch] += 1;
    await this.save(save);
    return save;
  }

  async selectCore(coreId: CoreId): Promise<MetaSaveV3> {
    const save = await this.load();
    if (!isCoreUnlocked(coreId, save)) return save;
    save.selectedCoreId = coreId;
    await this.save(save);
    return save;
  }

  async updateCosmetics(cosmetics: Partial<CosmeticLoadout>): Promise<MetaSaveV3> {
    const save = await this.load();
    save.cosmetics = {
      coreSkinId: isCoreSkinId(cosmetics.coreSkinId)
        ? cosmetics.coreSkinId
        : save.cosmetics.coreSkinId,
      skillSkinId: isSkillSkinId(cosmetics.skillSkinId)
        ? cosmetics.skillSkinId
        : save.cosmetics.skillSkinId,
    };
    await this.save(save);
    return save;
  }

  async updateSettings(settings: Partial<PlayerSettings>): Promise<MetaSaveV3> {
    const save = await this.load();
    save.settings = { ...save.settings, ...settings };
    await this.save(save);
    return save;
  }

  async reset(): Promise<MetaSaveV3> {
    const save = createDefaultSave();
    await this.save(save);
    return save;
  }

  private database(): Promise<IDBPDatabase> {
    this.databasePromise ??= openDB('neocore-save', SAVE_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains('player')) database.createObjectStore('player');
      },
    });
    return this.databasePromise;
  }
}
